<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const { t } = useI18n();

const integrationLoaded = ref(false);
const installedLanguages = ref([]);
const availableLanguages = ref([]);
const showAvailable = ref(false);
const loadingLanguages = ref(false);
const downloadingPairs = ref({});
const errorMessage = ref('');
const successMessage = ref('');
const showConnectForm = ref(false);
const connectUrl = ref('');
const connectApiKey = ref('');
const dialogRef = ref(null);

const integration = computed(() => {
  return store.getters['integrations/getIntegration']('libre_translate');
});

const hook = computed(() => {
  const { hooks = [] } = integration.value || {};
  return hooks[0] || {};
});

const isConnected = computed(() => {
  const { hooks = [] } = integration.value || {};
  return hooks.length > 0;
});

const ltUrl = computed(() => {
  const url = hook.value?.settings?.url || '';
  return url.replace(/\/+$/, '');
});

const apiKey = computed(() => {
  return hook.value?.settings?.api_key || '';
});

async function fetchInstalledLanguages() {
  if (!ltUrl.value || !apiKey.value) return;
  try {
    const response = await fetch(
      `${ltUrl.value}/installed-packages?api_key=${encodeURIComponent(apiKey.value)}`
    );
    if (response.ok) {
      const data = await response.json();
      installedLanguages.value = data.installed || [];
    }
  } catch (e) {
    console.error('Error fetching installed languages:', e);
  }
}

async function fetchAvailableLanguages() {
  if (!ltUrl.value || !apiKey.value) return;
  loadingLanguages.value = true;
  try {
    const response = await fetch(
      `${ltUrl.value}/available-packages?api_key=${encodeURIComponent(apiKey.value)}`
    );
    if (response.ok) {
      const data = await response.json();
      // Flatten nested languages+targets into from/to pairs
      const pairs = [];
      for (const lang of (data.languages || [])) {
        for (const target of (lang.targets || [])) {
          pairs.push({
            from_code: lang.code,
            from_name: lang.name,
            to_code: target.code,
            to_name: target.name,
            installed: target.installed || false,
          });
        }
      }
      availableLanguages.value = pairs;
    }
  } catch (e) {
    console.error('Error fetching available languages:', e);
  } finally {
    loadingLanguages.value = false;
  }
}

async function installPackage(fromCode, toCode) {
  const key = `${fromCode}-${toCode}`;
  downloadingPairs.value = { ...downloadingPairs.value, [key]: 'downloading' };
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const params = new URLSearchParams({
      api_key: apiKey.value,
      source: fromCode,
      target: toCode,
    });
    const response = await fetch(
      `${ltUrl.value}/install-package?${params}`,
      { method: 'POST' }
    );
    if (response.ok) {
      pollDownloadStatus(key);
    } else {
      const err = await response.json();
      downloadingPairs.value = { ...downloadingPairs.value, [key]: 'error' };
      errorMessage.value = err.error || 'Error installing package';
    }
  } catch (e) {
    downloadingPairs.value = { ...downloadingPairs.value, [key]: 'error' };
    errorMessage.value = e.message;
  }
}

function pollDownloadStatus(key) {
  const interval = setInterval(async () => {
    try {
      const response = await fetch(
        `${ltUrl.value}/download-status?api_key=${encodeURIComponent(apiKey.value)}`
      );
      if (response.ok) {
        const data = await response.json();
        const status = data.downloads?.[key];
        if (status === 'completed') {
          clearInterval(interval);
          downloadingPairs.value = { ...downloadingPairs.value, [key]: 'completed' };
          successMessage.value = `${key} installed successfully`;
          fetchInstalledLanguages();
          fetchAvailableLanguages();
        } else if (status && status.startsWith('error')) {
          clearInterval(interval);
          downloadingPairs.value = { ...downloadingPairs.value, [key]: 'error' };
          errorMessage.value = status;
        }
      }
    } catch (e) {
      clearInterval(interval);
      downloadingPairs.value = { ...downloadingPairs.value, [key]: 'error' };
    }
  }, 3000);
}

async function removePackage(fromCode, toCode) {
  const key = `${fromCode}-${toCode}`;
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const rmParams = new URLSearchParams({
      api_key: apiKey.value,
      source: fromCode,
      target: toCode,
    });
    const response = await fetch(
      `${ltUrl.value}/remove-package?${rmParams}`,
      { method: 'POST' }
    );
    if (response.ok) {
      successMessage.value = `${key} removed`;
      fetchInstalledLanguages();
      fetchAvailableLanguages();
    } else {
      const err = await response.json();
      errorMessage.value = err.error || 'Error removing package';
    }
  } catch (e) {
    errorMessage.value = e.message;
  }
}

function toggleAvailable() {
  showAvailable.value = !showAvailable.value;
  if (showAvailable.value && availableLanguages.value.length === 0) {
    fetchAvailableLanguages();
  }
}

function getStatusForPair(fromCode, toCode) {
  return downloadingPairs.value[`${fromCode}-${toCode}`] || null;
}

async function connectIntegration() {
  if (!connectUrl.value) {
    errorMessage.value = 'LibreTranslate URL is required';
    return;
  }
  try {
    await store.dispatch('integrations/createHook', {
      app_id: 'libre_translate',
      settings: {
        url: connectUrl.value.replace(/\/+$/, ''),
        api_key: connectApiKey.value,
      },
    });
    showConnectForm.value = false;
    useAlert('LibreTranslate connected successfully');
    fetchInstalledLanguages();
  } catch (error) {
    errorMessage.value = error?.message || 'Error connecting LibreTranslate';
  }
}

function openDisconnectDialog() {
  dialogRef.value?.open();
}

async function disconnectIntegration() {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: 'libre_translate',
      hookId: hook.value.id,
    });
    dialogRef.value?.close();
    useAlert('LibreTranslate disconnected');
    installedLanguages.value = [];
    availableLanguages.value = [];
    showAvailable.value = false;
  } catch (error) {
    errorMessage.value = error?.message || 'Error disconnecting';
  }
}

onMounted(async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
  if (isConnected.value) {
    fetchInstalledLanguages();
  }
});
</script>

<template>
  <div
    v-if="integrationLoaded"
    class="flex flex-col flex-1 overflow-auto gap-5 pt-1 pb-10"
  >
    <!-- Integration header card -->
    <div
      class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow gap-6"
    >
      <div
        class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
      >
        <div class="flex h-16 w-16 items-center justify-center flex-shrink-0">
          <img
            :src="`/dashboard/images/integrations/${integration.id || 'libre_translate'}.png`"
            class="max-w-full rounded-md border border-n-weak shadow-sm bg-n-alpha-3"
          />
        </div>
        <div>
          <h3 class="mb-1 text-xl font-medium text-n-slate-12">
            {{ integration.name || 'LibreTranslate' }}
          </h3>
          <p class="text-n-slate-11 text-sm leading-6">
            {{ integration.description || 'Free and open-source automatic translation for customer messages.' }}
          </p>
        </div>
      </div>
      <div class="flex justify-center items-center mb-0">
        <Button
          v-if="isConnected"
          faded
          ruby
          label="Disconnect"
          @click="openDisconnectDialog"
        />
        <Button
          v-else
          faded
          blue
          label="Connect"
          @click="showConnectForm = !showConnectForm"
        />
      </div>
    </div>

    <!-- Connect form -->
    <div
      v-if="showConnectForm && !isConnected"
      class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
    >
      <h4 class="text-base font-medium text-n-slate-12 mb-4">
        Connect LibreTranslate
      </h4>
      <div class="flex flex-col gap-3">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            LibreTranslate URL *
          </label>
          <input
            v-model="connectUrl"
            type="text"
            placeholder="https://libretranslate.example.com"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            Translation API Key (optional)
          </label>
          <input
            v-model="connectApiKey"
            type="text"
            placeholder="API key for translations"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>
        <div class="flex gap-2 mt-2">
          <Button blue label="Connect" @click="connectIntegration" />
          <Button
            faded
            slate
            label="Cancel"
            @click="showConnectForm = false"
          />
        </div>
      </div>
    </div>

    <!-- Messages -->
    <div
      v-if="errorMessage"
      class="p-3 rounded-md bg-n-ruby-2 text-n-ruby-11 text-sm flex items-center justify-between"
    >
      <span>{{ errorMessage }}</span>
      <button class="ml-2 text-n-ruby-11 font-bold" @click="errorMessage = ''">
        x
      </button>
    </div>
    <div
      v-if="successMessage"
      class="p-3 rounded-md bg-n-teal-2 text-n-teal-11 text-sm flex items-center justify-between"
    >
      <span>{{ successMessage }}</span>
      <button
        class="ml-2 text-n-teal-11 font-bold"
        @click="successMessage = ''"
      >
        x
      </button>
    </div>

    <!-- Languages panel (only when connected) -->
    <template v-if="isConnected">
      <!-- Connection info -->
      <div
        class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
      >
        <h4 class="text-base font-medium text-n-slate-12 mb-2">
          Connection
        </h4>
        <p class="text-sm text-n-slate-11">
          <span class="font-medium">URL:</span> {{ ltUrl }}
        </p>
      </div>

      <!-- Installed languages -->
      <div
        class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
      >
        <h4 class="text-base font-medium text-n-slate-12 mb-3">
          Installed Languages
        </h4>
        <div
          v-if="installedLanguages.length === 0"
          class="text-sm text-n-slate-10"
        >
          Loading languages...
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <span
            v-for="lang in installedLanguages"
            :key="lang.code"
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-n-alpha-5 text-n-slate-12"
          >
            {{ lang.name }}
            <span class="ml-1 text-xs text-n-slate-10 uppercase">
              ({{ lang.code }})
            </span>
          </span>
        </div>
        <p class="mt-3 text-xs text-n-slate-10">
          {{ installedLanguages.length }} languages installed
        </p>
      </div>

      <!-- Download more languages -->
      <div
        class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
      >
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-base font-medium text-n-slate-12">
            Download Languages
          </h4>
          <Button
            faded
            :blue="!showAvailable"
            :slate="showAvailable"
            :label="showAvailable ? 'Hide' : 'Show available'"
            @click="toggleAvailable"
          />
        </div>

        <div v-if="showAvailable">
          <div v-if="loadingLanguages" class="flex items-center gap-2 py-4">
            <Spinner size="small" color-scheme="primary" />
            <span class="text-sm text-n-slate-10">
              Loading available packages...
            </span>
          </div>

          <div v-else-if="availableLanguages.length === 0" class="py-4">
            <p class="text-sm text-n-slate-10">
              No packages found. Make sure the API Key is configured.
            </p>
          </div>

          <div v-else class="overflow-x-auto max-h-96 overflow-y-auto">
            <table class="w-full text-sm">
              <thead class="sticky top-0 bg-n-alpha-3">
                <tr class="border-b border-n-container">
                  <th
                    class="text-left py-2 px-3 text-n-slate-11 font-medium"
                  >
                    From
                  </th>
                  <th
                    class="text-left py-2 px-3 text-n-slate-11 font-medium"
                  >
                    To
                  </th>
                  <th
                    class="text-left py-2 px-3 text-n-slate-11 font-medium"
                  >
                    Status
                  </th>
                  <th
                    class="text-right py-2 px-3 text-n-slate-11 font-medium"
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="pkg in availableLanguages"
                  :key="`${pkg.from_code}-${pkg.to_code}`"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-2 px-3">
                    <span class="font-medium">{{ pkg.from_name }}</span>
                    <span class="text-xs text-n-slate-10 ml-1">
                      ({{ pkg.from_code }})
                    </span>
                  </td>
                  <td class="py-2 px-3">
                    <span class="font-medium">{{ pkg.to_name }}</span>
                    <span class="text-xs text-n-slate-10 ml-1">
                      ({{ pkg.to_code }})
                    </span>
                  </td>
                  <td class="py-2 px-3">
                    <span
                      v-if="pkg.installed"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-teal-3 text-n-teal-11"
                    >
                      Installed
                    </span>
                    <span
                      v-else-if="getStatusForPair(pkg.from_code, pkg.to_code) === 'downloading'"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-amber-3 text-n-amber-11"
                    >
                      Downloading...
                    </span>
                    <span
                      v-else-if="getStatusForPair(pkg.from_code, pkg.to_code) === 'completed'"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-teal-3 text-n-teal-11"
                    >
                      Installed
                    </span>
                    <span
                      v-else-if="getStatusForPair(pkg.from_code, pkg.to_code) === 'error'"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-ruby-3 text-n-ruby-11"
                    >
                      Error
                    </span>
                    <span v-else class="text-xs text-n-slate-10">
                      Not installed
                    </span>
                  </td>
                  <td class="py-2 px-3 text-right">
                    <Button
                      v-if="pkg.installed || getStatusForPair(pkg.from_code, pkg.to_code) === 'completed'"
                      faded
                      ruby
                      xs
                      label="Remove"
                      @click="removePackage(pkg.from_code, pkg.to_code)"
                    />
                    <Button
                      v-else-if="getStatusForPair(pkg.from_code, pkg.to_code) === 'downloading'"
                      faded
                      slate
                      xs
                      label="Downloading..."
                      disabled
                    />
                    <Button
                      v-else
                      faded
                      blue
                      xs
                      label="Install"
                      @click="installPackage(pkg.from_code, pkg.to_code)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p v-if="!showAvailable" class="text-sm text-n-slate-10">
          Click "Show available" to browse and download language packages.
        </p>
      </div>
    </template>

    <!-- Disconnect dialog -->
    <Dialog
      ref="dialogRef"
      type="alert"
      title="Disconnect LibreTranslate"
      description="Are you sure you want to disconnect LibreTranslate? Translation features will stop working."
      confirm-button-label="Disconnect"
      cancel-button-label="Cancel"
      @confirm="disconnectIntegration"
    />
  </div>
  <div v-else class="flex items-center justify-center flex-1">
    <Spinner size="" color-scheme="primary" />
  </div>
</template>
